import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonIcon from '@mui/icons-material/Person';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LanguageIcon from '@mui/icons-material/Language';
import FolderIcon from '@mui/icons-material/Folder';

function ProductListItem({ result, index, totalResults }) {
  const { requested_key, normalized_key, found, product } = result;

  const getBackgroundColor = () => {
    if (found) {
      return 'rgba(76, 175, 80, 0.05)';
    }
    return 'rgba(244, 67, 54, 0.05)';
  };

  const getAuthorsDisplay = () => {
    const holders = product?.copyright_holders;
    if (!holders) return null;

    const editors = holders.editors || [];
    const authors = holders.authors || [];
    const contributors = holders.contributors || [];

    const allPeople = [...editors, ...authors, ...contributors];
    if (allPeople.length === 0) return null;

    const firstPerson = allPeople[0];
    const name = `${firstPerson.first_name || ''} ${firstPerson.last_name || ''}`.trim();

    if (allPeople.length === 1) {
      return name;
    }
    return `${name} et al.`;
  };

  const authorsDisplay = found ? getAuthorsDisplay() : null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        p: 2,
        borderBottom: index !== totalResults - 1 ? '1px solid #eee' : 'none',
        backgroundColor: getBackgroundColor(),
        gap: 2,
      }}
    >
      {/* Status icon */}
      <Box sx={{ flexShrink: 0, pt: 0.5 }}>
        {found ? (
          <CheckCircleIcon color="success" />
        ) : (
          <CancelIcon color="error" />
        )}
      </Box>

      {/* Key info */}
      <Box sx={{ minWidth: 120, flexShrink: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
          {requested_key}
        </Typography>
        {requested_key !== normalized_key && (
          <Typography variant="caption" color="text.secondary">
            {normalized_key}
          </Typography>
        )}
      </Box>

      {/* Product info or not found message */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {found && product ? (
          <>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {product.title || 'Untitled'}
            </Typography>
            {authorsDisplay && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}
              >
                <PersonIcon sx={{ fontSize: '0.8rem' }} />
                {authorsDisplay}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
              {product.ean && (
                <Chip
                  label={`EAN: ${product.ean}`}
                  size="small"
                  variant="outlined"
                  sx={{ height: 22, fontSize: '0.7rem' }}
                />
              )}
              {product.pub_type && (
                <Chip
                  icon={<MenuBookIcon sx={{ fontSize: '0.9rem' }} />}
                  label={product.pub_type}
                  size="small"
                  color="primary"
                  sx={{ height: 22, fontSize: '0.7rem' }}
                />
              )}
              {product.isbn && (
                <Chip
                  label={`ISBN: ${product.isbn}`}
                  size="small"
                  variant="outlined"
                  sx={{ height: 22, fontSize: '0.7rem' }}
                />
              )}
              {product.copyright_year && (
                <Chip
                  icon={<CalendarTodayIcon sx={{ fontSize: '0.9rem' }} />}
                  label={product.copyright_year}
                  size="small"
                  variant="outlined"
                  sx={{ height: 22, fontSize: '0.7rem' }}
                />
              )}
              {product.language_term && (
                <Chip
                  icon={<LanguageIcon sx={{ fontSize: '0.9rem' }} />}
                  label={product.language_term}
                  size="small"
                  variant="outlined"
                  color="info"
                  sx={{ height: 22, fontSize: '0.7rem' }}
                />
              )}
              {product.digital_asset_collection_key && (
                <Chip
                  icon={<FolderIcon sx={{ fontSize: '0.9rem' }} />}
                  label={product.digital_asset_collection_key}
                  size="small"
                  variant="outlined"
                  color="secondary"
                  sx={{ height: 22, fontSize: '0.7rem' }}
                />
              )}
            </Box>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Not found in database
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default ProductListItem;
